import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
// The row shape IAM-02 publishes, imported rather than restated.
import type { WorkforceMember } from "@tdelivr/api-client/generated/identity/workforce-identity.js";

import { boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import {
  INVITE_REFUSAL,
  matchesState,
  memberRow,
  STAFF_READ_SCOPE,
  statesPresent,
} from "../staff.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Staff and roles: who can sign in to this tenant's consoles.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `staff`, against IAM-02
 * `GET /v1/tenants/{tenantId}/workforce-members` — an operation that did not exist, and could not
 * have been written, until something on this platform modelled a person. ADR 0084 is that.
 *
 * **This was the console's deepest blocker and the only one of its kind.** Every other page here
 * was waiting on a route over data that already existed; this one was waiting on the noun. The
 * recorded reason named all three columns and it was accurate about each. `src/staff.ts` carries
 * what is now real and what still is not, and the short version is: `Person` and `Role` are
 * published, `Last sign-in` is not, three of four row states are published and `2-step missing` is
 * not, and no tile has a source.
 *
 * **`roles` beside it stays blocked and that is correct.** The design's own closing note on this
 * page — "Roles bundle permissions; a person's effective permissions are shown on their page" — is
 * two different things on two different pages, and this one publishes the first. A member's `role`
 * is the tenant's own words and is deliberately not a reference to an IAM-03 policy; ADR 0084 §2.
 *
 * **The filter narrows rows rather than refetching.** The listing does take a `status` parameter —
 * unlike the roles board's, which takes none — so refetching was available and was not chosen: the
 * rows are already bounded at 200 and a refetch per filter change would make the bound apply
 * separately to each state, so switching the filter twice could show a different set than switching
 * it once. Narrowing the fetched rows keeps one bound for the whole board, and the bounded note
 * says what that bound is.
 */

const workforce = services.identity__workforce_identity;

export interface StaffProps {
  readonly transport: Transport;
  /**
   * The tenant whose staff to read.
   *
   * A required prop rather than a value this component reaches for, exactly as the roles board
   * takes one: the route is addressed by `tenantId`, IAM-02 refuses a path naming a tenant other
   * than the one on the token with a 403, and a component that read the session itself would be
   * deciding which tenant it acts for — which `apps/README.md` forbids.
   */
  readonly tenantId: string;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
}

interface Loaded {
  readonly members: readonly WorkforceMember[];
  readonly truncated: boolean;
}

export function Staff({ transport, tenantId, crumbs }: StaffProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const client = workforce.createWorkforceIdentityClient(transport);
    try {
      /*
       * No `merchantId` and no `locationId`. A tenant administrator holds neither, and this board
       * is every person the tenant employs — the shop staff the merchant portal's board draws are
       * among these rows, with their merchant in the detail panel. That is why those two are query
       * filters rather than path segments: one operation answers both boards.
       */
      const answered = await client.listWorkforceMembers(tenantId, PER_FETCH);
      setLoaded({
        members: answered.members,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, [STAFF_READ_SCOPE]));
      setLoaded(undefined);
    }
  }, [transport, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    return loaded.members
      .filter((member) => matchesState(member, status))
      .map((member) => memberRow(member));
  }, [loaded, status]);

  const bounded = boundedNote(
    loaded?.members.length,
    loaded?.truncated ?? false,
    "people",
    "newest first",
  );

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Staff and roles"
      source="IAM-02"
      // The design's own sentence, word for word. Nothing in it promises a control that is absent.
      description="Who can sign in to your consoles and what they can do."
      columns={["Person", "Role", "Last sign-in"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="A person appears here as soon as one is invited for this tenant."
      primary={{ label: "Invite staff", refusal: INVITE_REFUSAL }}
      filter={{
        unfiltered: "All states",
        options: loaded === undefined ? [] : statesPresent(loaded.members),
        value: status,
        onChange: setStatus,
      }}
      note={{
        /*
         * The design's `info` note is kept word for word, and this is the one page on this console
         * where the designed note needed no replacement: "Roles bundle permissions; a person's
         * effective permissions are shown on their page." Both halves are true here. A member's
         * role is words the tenant wrote, which is what "bundle" describes from an administrator's
         * side, and the permissions page it points at is `roles` in the same nav group — blocked,
         * and the note is a statement about where a thing lives rather than a promise that this
         * board shows it.
         */
        kind: "info",
        glyph: "info",
        text: "Roles bundle permissions; a person's effective permissions are shown on their page.",
      }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote="Every person this tenant employs, newest first, including the staff of its merchants — each one's shop and branch are in the detail panel. The design's fourth state, 2-step missing, is not drawn: whether somebody has enrolled a second factor is the token issuer's to say, so a person without one appears here as active."
      exportName="staff-and-roles"
      detailSubtitle="Staff and roles · IAM-02"
      detailNote="Roles bundle permissions; a person's effective permissions are shown on their page."
    />
  );
}
